// services/NotificationScheduler.js - Version améliorée
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  addDoc,
  updateDoc,
  doc,
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../firebase/config';
import NotificationDispatcher from './NotificationDispatcher';
import TimelineDelayService from './TimelineDelayService';

class NotificationScheduler {
  constructor() {
    this.isRunning = false;
    this.checkInterval = 5 * 60 * 1000; // Vérifier toutes les 5 minutes
    this.dailyCheckHour = 9; // Vérification quotidienne à 9h
    this.lastDailyCheck = null;
  }

  // Démarrer le scheduler
  start() {
    if (this.isRunning) return;
    
    this.isRunning = true;
    this.scheduleInterval = setInterval(() => {
      this.checkScheduledNotifications();
    }, this.checkInterval);
    
    console.log('📅 SCHEDULER: Notification scheduler démarré');
    
    // Effectuer une vérification immédiate
    this.checkScheduledNotifications();
  }

  // Arrêter le scheduler
  stop() {
    if (this.scheduleInterval) {
      clearInterval(this.scheduleInterval);
    }
    this.isRunning = false;
    console.log('📅 SCHEDULER: Notification scheduler arrêté');
  }

  // Vérifier les notifications programmées
  async checkScheduledNotifications() {
    try {
      const now = new Date();
      
      // Vérification quotidienne des délais de timeline (une fois par jour à 9h)
      if (this.shouldPerformDailyCheck(now)) {
        console.log('📅 SCHEDULER: Début de la vérification quotidienne des délais timeline');
        await TimelineDelayService.checkAllDossierDelays();
        this.lastDailyCheck = now;
      }
      
      // Vérifier les échéances de dossiers (existant)
      await this.checkDossierDeadlines();
      
      // Vérifier les rappels programmés (existant)
      await this.checkScheduledReminders();
      
    } catch (error) {
      console.error('❌ SCHEDULER: Erreur lors de la vérification des notifications:', error);
    }
  }

  // Vérifier si on doit effectuer la vérification quotidienne
  shouldPerformDailyCheck(now) {
    // Si c'est la première fois
    if (!this.lastDailyCheck) {
      return true;
    }

    // Si on a passé l'heure de vérification quotidienne et qu'on ne l'a pas encore fait aujourd'hui
    const currentHour = now.getHours();
    const lastCheckDate = this.lastDailyCheck.toDateString();
    const todayDate = now.toDateString();
    
    return (
      currentHour >= this.dailyCheckHour && 
      lastCheckDate !== todayDate
    );
  }

  // ✅ NOUVELLE MÉTHODE : Vérifier un dossier spécifique après mise à jour de timeline
  async checkDossierAfterTimelineUpdate(dossierId) {
    try {
      console.log(`🔄 SCHEDULER: Vérification dossier ${dossierId} après mise à jour timeline`);
      
      // Vérifier si le dossier peut sortir des retards
      await TimelineDelayService.checkDossierRecovery(dossierId);
      
    } catch (error) {
      console.error(`❌ SCHEDULER: Erreur vérification dossier ${dossierId}:`, error);
    }
  }

  // ✅ NOUVELLE MÉTHODE : Forcer une vérification des délais
  async forceTimelineDelayCheck() {
    try {
      console.log('🔍 SCHEDULER: Vérification forcée des délais timeline');
      await TimelineDelayService.checkAllDossierDelays();
      console.log('✅ SCHEDULER: Vérification forcée terminée');
    } catch (error) {
      console.error('❌ SCHEDULER: Erreur lors de la vérification forcée:', error);
    }
  }

  // Vérifier les échéances de dossiers (CODE EXISTANT - INCHANGÉ)
  async checkDossierDeadlines() {
    try {
      const now = new Date();
      const weekFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
      
      // Récupérer les dossiers avec échéance proche
      const dossiersQuery = query(
        collection(db, 'dossiers'),
        where('echeance', '<=', weekFromNow),
        where('status', '!=', 'closed')
      );
      
      const dossiersSnapshot = await getDocs(dossiersQuery);
      
      for (const dossierDoc of dossiersSnapshot.docs) {
        const dossier = { id: dossierDoc.id, ...dossierDoc.data() };
        const echeance = dossier.echeance.toDate();
        const daysRemaining = Math.ceil((echeance - now) / (1000 * 60 * 60 * 24));
        
        if (daysRemaining >= 0 && daysRemaining <= 7) {
          // Vérifier si on a déjà envoyé une notification pour cette échéance
          const alreadyNotified = await this.checkIfAlreadyNotified(
            dossier.id, 
            'deadline_reminder', 
            daysRemaining
          );
          
          if (!alreadyNotified) {
            // Notifier le responsable du dossier
            if (dossier.assignedTo) {
              await NotificationDispatcher.notifyDeadlineApproaching(
                dossier, 
                dossier.assignedTo, 
                daysRemaining
              );
            }
            
            // Notifier le client si c'est dans les 3 jours
            if (daysRemaining <= 3 && dossier.clientId) {
              await NotificationDispatcher.notifyDeadlineApproaching(
                dossier, 
                dossier.clientId, 
                daysRemaining
              );
            }
            
            // Marquer comme notifié
            await this.markAsNotified(
              dossier.id, 
              'deadline_reminder', 
              daysRemaining
            );
          }
        }
      }
    } catch (error) {
      console.error('❌ SCHEDULER: Erreur vérification échéances:', error);
    }
  }

  // Vérifier si une notification a déjà été envoyée (CODE EXISTANT - INCHANGÉ)
  async checkIfAlreadyNotified(entityId, type, context) {
    try {
      const notificationsQuery = query(
        collection(db, 'notification_logs'),
        where('entityId', '==', entityId),
        where('type', '==', type),
        where('context', '==', context)
      );
      
      const snapshot = await getDocs(notificationsQuery);
      return !snapshot.empty;
    } catch (error) {
      console.error('❌ SCHEDULER: Erreur vérification notification:', error);
      return false;
    }
  }

  // Marquer une notification comme envoyée (CODE EXISTANT - INCHANGÉ)
  async markAsNotified(entityId, type, context) {
    try {
      await addDoc(collection(db, 'notification_logs'), {
        entityId,
        type,
        context,
        sentAt: serverTimestamp()
      });
    } catch (error) {
      console.error('❌ SCHEDULER: Erreur marquage notification:', error);
    }
  }

  // Programmer une notification personnalisée (CODE EXISTANT - INCHANGÉ)
  async scheduleNotification(notification, sendAt) {
    try {
      await addDoc(collection(db, 'scheduled_notifications'), {
        ...notification,
        scheduledFor: sendAt,
        sent: false,
        createdAt: serverTimestamp()
      });
    } catch (error) {
      console.error('❌ SCHEDULER: Erreur programmation notification:', error);
    }
  }

  // Vérifier les rappels programmés (CODE EXISTANT - INCHANGÉ)
  async checkScheduledReminders() {
    try {
      const now = new Date();
      
      const remindersQuery = query(
        collection(db, 'scheduled_notifications'),
        where('scheduledFor', '<=', now),
        where('sent', '==', false)
      );
      
      const remindersSnapshot = await getDocs(remindersQuery);
      
      for (const reminderDoc of remindersSnapshot.docs) {
        const reminder = { id: reminderDoc.id, ...reminderDoc.data() };
        
        try {
          await NotificationDispatcher.sendNotification(reminder);
          
          // Marquer comme envoyé
          await updateDoc(doc(db, 'scheduled_notifications', reminder.id), {
            sent: true,
            sentAt: serverTimestamp()
          });
        } catch (error) {
          console.error('❌ SCHEDULER: Erreur envoi rappel programmé:', error);
        }
      }
    } catch (error) {
      console.error('❌ SCHEDULER: Erreur vérification rappels:', error);
    }
  }

  // ✅ NOUVELLE MÉTHODE : Obtenir des statistiques des délais
  async getDelayStatistics() {
    try {
      // Compter les dossiers en retard
      const overdueQuery = query(
        collection(db, 'dossiers'),
        where('status', '==', 'en retard')
      );
      
      const overdueSnapshot = await getDocs(overdueQuery);
      const overdueCount = overdueSnapshot.docs.length;

      // Compter les notifications de retard envoyées aujourd'hui
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      const notificationsQuery = query(
        collection(db, 'notifications'),
        where('type', '==', 'timeline_delay'),
        where('createdAt', '>=', today)
      );
      
      const notificationsSnapshot = await getDocs(notificationsQuery);
      const todayNotifications = notificationsSnapshot.docs.length;

      return {
        overdueCount,
        todayNotifications,
        lastCheck: this.lastDailyCheck
      };

    } catch (error) {
      console.error('❌ SCHEDULER: Erreur statistiques:', error);
      return {
        overdueCount: 0,
        todayNotifications: 0,
        lastCheck: null
      };
    }
  }
}

export default new NotificationScheduler();